import { LocalizationProvider } from '@mui/x-date-pickers'
import TextInput from '../../Form/Inputs/TextInput'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Control, Controller, UseFormGetValues, UseFormWatch } from 'react-hook-form'
import { Stack, Box, Typography } from '@mui/material'
import { t } from 'i18next'
import dayjs from 'dayjs'
import i18n from '../../../config/i18n'
import SelectInput, { Option } from '../../Form/Inputs/SelectInput'
import ProjectSelector from '../../Form/Inputs/ProjectSelector'
import { CostFormValues } from '../../../store/types/form/CostFormValues'
import CostType from '../../../store/types/enums/CostType'
import CheckboxInput from '../../Form/Inputs/CheckboxInput'
import CollapsibleSection from '../../Button/CollapsibleSection'
import DateRangePickerWithPresets from '../../DateRangePickerWithPresets/DateRangePickerWithPresets'
import React, { useMemo } from 'react'
import { Trans } from 'react-i18next'
import NumericInput from '../../Form/Inputs/NumericInput'

type CostViewFormProps = {
  control: Control<CostFormValues>
  getValues: UseFormGetValues<CostFormValues>
  watch: UseFormWatch<CostFormValues>
  isEdit: boolean
}

export const costTypeOptions: Option[] = [
  { value: CostType.HOTEL, label: 'components.costSidebar.types.hotel' },
  { value: CostType.LICENSE, label: 'components.costSidebar.types.license' },
  { value: CostType.FUEL, label: 'components.costSidebar.types.fuel' },
  { value: CostType.SHOPPING, label: 'components.costSidebar.types.shopping' },
  { value: CostType.OTHER, label: 'components.costSidebar.types.other' },
]

const CostViewForm = ({ control, getValues, watch, isEdit }: CostViewFormProps) => {
  const endDate = watch('endDate')
  const startDate = watch('startDate')

  const isEndDateInNextMonth = useMemo(() => {
    if (!endDate || !startDate) return false

    return (
      endDate.year() > startDate.year() || (endDate.year() === startDate.year() && endDate.month() > startDate.month())
    )
  }, [endDate, startDate])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
      <Stack gap={4}>
        <TextInput
          name={'principal'}
          control={control}
          label={t('components.costSidebar.table.header.principal')}
          maxLength={20}
        />

        <Controller
          name={'projectId'}
          control={control}
          render={({ field: { onChange, value } }) => {
            const selectedOption = value ? ({ label: value, value: value } as Option) : null
            return (
              <ProjectSelector
                value={selectedOption}
                onChange={opt => onChange(opt ? opt.value : null)}
                label={t('components.costSidebar.form.project')}
                loggable={true}
              />
            )
          }}
        />

        <SelectInput
          options={costTypeOptions}
          name={'type'}
          control={control}
          label={`${t('components.costSidebar.table.header.type')} *`}
          rules={{ required: t('components.costSidebar.form.errors.typeRequired') }}
        />

        <NumericInput
          name={'amount'}
          required
          control={control}
          numericType={'decimal'}
          unitOfMeasure={'zł'}
          label={t('components.costSidebar.table.header.amount')}
          placeholder={t('components.costSidebar.table.header.amount')}
          minValue={0.01}
          maxValue={9999.99}
          maxLength={7}
          rules={{
            required: t('components.costSidebar.form.errors.amountRequired'),
            min: { value: 0.01, message: t('components.costSidebar.form.errors.amountMin', { value: 0.01 }) },
            max: { value: 9999.99, message: t('components.costSidebar.form.errors.amountMax', { value: 9999.99 }) },
          }}
        />

        <TextInput
          name={'quantity'}
          required
          control={control}
          numericType={'natural'}
          maxLength={5}
          minValue={1}
          label={t('components.costSidebar.table.body.quantity')}
          rules={{
            required: t('components.costSidebar.form.errors.quantityRequired'),
            min: { value: 1, message: t('components.costSidebar.form.errors.quantityMin', { value: 1 }) },
          }}
        />
        <Controller
          name={'startDate'}
          control={control}
          rules={{
            required: t('components.costSidebar.form.errors.startDateRequired'),
            validate: {
              valid: date => {
                if (!date) return true
                return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
              },
            },
          }}
          render={({ field: { onChange: onStartChange, value: startValue }, fieldState: { error: startError } }) => (
            <Controller
              name={'endDate'}
              control={control}
              rules={{
                required: t('components.costSidebar.form.errors.endDateRequired'),
                validate: {
                  valid: date => {
                    if (date === null || date === undefined) return true
                    return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
                  },
                  endDateAfterStartDate: value => {
                    if (dayjs.isDayjs(value)) {
                      const startDate = getValues('startDate')
                      if (value.isBefore(startDate)) {
                        return t('components.costSidebar.form.errors.endDateBefore')
                      }
                    }
                    return true
                  },
                },
              }}
              render={({ field: { onChange: onEndChange, value: endValue }, fieldState: { error: endError } }) => (
                <Box>
                  <DateRangePickerWithPresets
                    startDate={startValue ?? null}
                    endDate={endValue ?? null}
                    setStartDate={onStartChange}
                    setEndDate={onEndChange}
                    startDateLabel={`${t('components.costSidebar.table.body.startDate')} *`}
                    endDateLabel={`${t('components.costSidebar.table.body.endDate')} *`}
                  />
                  {startError && (
                    <Typography variant="caption" color="error">
                      {startError.message}
                    </Typography>
                  )}
                  {endError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      {endError.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
          )}
        />

        {isEndDateInNextMonth && (
          <Typography variant="caption">
            <Trans i18nKey="components.costSidebar.duplicateCaption" components={{ bold: <strong /> }} />
          </Typography>
        )}

        {!isEdit && (
          <CollapsibleSection
            text={t('components.costSidebar.form.manyCostsAtOnce')}
            contextHelpText={t('components.costSidebar.form.manyCostsAtOnceContext')}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2.5, justifyContent: 'space-between', pt: 0.5 }}>
              <Box sx={{ display: 'flex', flexShrink: 0 }}>
                <CheckboxInput name={'monthRepeat'} control={control} label={t('components.costSidebar.form.addFor')} />
              </Box>

              <Box sx={{ display: 'flex', flexGrow: 1 }}>
                <TextInput
                  name={'monthRepeatAmount'}
                  control={control}
                  label={t('components.costSidebar.form.numberOfMonths')}
                  numericType={'natural'}
                  disabled={!watch('monthRepeat')}
                  maxValue={12}
                  maxLength={2}
                  rules={{
                    required: watch('monthRepeat')
                      ? t('components.costSidebar.form.errors.monthRepeatRequired')
                      : false,
                    max: {
                      value: 12,
                      message: t('components.costSidebar.form.errors.maxMonthAmount', { value: 12 }),
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexGrow: 1 }}>
                <TextInput
                  name={'currentMonthRepeatNumber'}
                  control={control}
                  label={t('components.costSidebar.form.startFromCostNumber')}
                  numericType={'natural'}
                  disabled={!watch('monthRepeat')}
                  maxValue={12}
                  maxLength={2}
                  rules={{
                    max: {
                      value: parseInt(watch('monthRepeatAmount') || '12'),
                      message: t('components.costSidebar.form.errors.maxMonthAmount', {
                        value: parseInt(watch('monthRepeatAmount') || '12'),
                      }),
                    },
                    min: {
                      value: 1,
                      message: t('components.costSidebar.form.errors.minMonthAmount', {
                        value: 1,
                      }),
                    },
                  }}
                />
              </Box>
            </Box>
          </CollapsibleSection>
        )}

        <TextInput
          name={'description'}
          control={control}
          label={t('components.table.header.description')}
          placeholder={t('page.projects.editProject.enterDescription')}
          maxLength={500}
          rules={{
            maxLength: { value: 500, message: t('page.worklog.form.descriptionLengthError') },
          }}
          multiline
          rows={9}
          showCharCounter
        />
      </Stack>
    </LocalizationProvider>
  )
}

export default CostViewForm
